import type {
  BookingCategory,
  BookingStatus,
  PaymentMethod,
  PaymentStatus,
} from "@/types/database";

export const CATEGORY_LABELS: Record<BookingCategory, string> = {
  care_home: "Care Home",
  private_event: "Private Event",
  wedding: "Wedding",
  corporate: "Corporate",
  other: "Other",
};

export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  scheduled: "Scheduled",
  cancelled: "Cancelled",
  archived: "Archived",
};

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  awaiting: "Awaiting Payment",
  paid: "Paid & Confirmed",
  attention: "Payment Attention Required",
  not_required: "No Payment Required",
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  stripe: "Stripe",
  bank_transfer: "Bank transfer",
  cash: "Cash",
  other: "Other",
};

/** Awaiting payment past its pay by date counts as needing attention. */
export function isOverdue(status: PaymentStatus, dueDate?: string | null): boolean {
  return status === "awaiting" && !!dueDate && daysUntil(dueDate) < 0;
}

export function effectiveStatus(status: PaymentStatus, dueDate?: string | null): PaymentStatus {
  return isOverdue(status, dueDate) ? "attention" : status;
}

export const DURATION_OPTIONS = [30, 45, 60, 90, 120, 150, 180];

export function durationLabel(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (!h) return `${m} minutes`;
  const hs = h === 1 ? "1 hour" : `${h} hours`;
  return m ? `${hs} ${m} minutes` : hs;
}

/** "18:00" + 45 -> "18:45" */
export function addMinutes(time: string, mins: number): string {
  const [h, m] = time.split(":").map(Number);
  const t = (h * 60 + m + mins) % (24 * 60);
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
}

/** Spec colours: green paid, amber awaiting, red attention, grey cancelled */
export function paymentStatusClass(rawStatus: PaymentStatus, bookingStatus: BookingStatus, dueDate?: string | null): string {
  if (bookingStatus === "cancelled") {
    return "bg-white/10 text-white/50 border-white/10";
  }
  const status = effectiveStatus(rawStatus, dueDate);
  switch (status) {
    case "paid":
      return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
    case "awaiting":
      return "bg-amber-500/15 text-amber-200 border-amber-500/30";
    case "attention":
      return "bg-red-500/15 text-red-300 border-red-500/30";
    case "not_required":
      return "bg-white/10 text-white/60 border-white/15";
    default:
      return "bg-white/10 text-white/60 border-white/10";
  }
}

export function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${dateStr}T00:00:00`);
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

/** Placeholder stored for bookings whose date is still to be confirmed. */
export const TBC_DATE = "2099-12-31";

export function formatDisplayDate(dateStr: string | null | undefined): string {
  if (!dateStr || dateStr === TBC_DATE) return "Date TBC";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "Europe/London",
    }).format(new Date(`${dateStr}T12:00:00`));
  } catch {
    return dateStr;
  }
}

export function formatTime(time: string | null): string {
  if (!time) return "—";
  return time.slice(0, 5);
}

export function defaultDueDate(performanceDate: string, daysBefore = 7): string {
  const d = new Date(`${performanceDate}T12:00:00`);
  d.setDate(d.getDate() - daysBefore);
  return d.toISOString().slice(0, 10);
}
